/**
 * DIY Christmas decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_christmas_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const diyChristmasDecorationsPack: ToilePack = {
    slug: 'diy-christmas-decorations',
    category: 'season',
    name: 'DIY Christmas decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'stars', label: 'Paper stars', subject: 'paper star lanterns in a window'},
        {key: 'wreath', label: 'Pinecone wreath', subject: 'a wreath of pinecones on a door'},
        {key: 'dough', label: 'Salt dough', subject: 'salt-dough ornaments on a tree'},
    ],
    styles: [
        {key: 'daylight', label: 'In daylight', style: 'the finished piece in place on a winter morning, soft daylight from camera-left, a warm balance of light and shadow', palette: 'natural paper, pine green, wood and red ribbon', framing: 'eye-level medium shot with a little room around it'},
        {key: 'evening', label: 'Evening glow', style: 'the same piece in the evening, warm glow of fairy lights from behind, a low dark-to-light balance with golden highlights', palette: 'amber light on natural materials, deep soft shadow', framing: 'eye-level medium shot, the background in soft bokeh'},
        {key: 'detail', label: 'Close-up', style: 'a close detail of the handmade texture with shallow depth of field, soft daylight from above, a bright balance', palette: 'the raw colors of the materials', framing: 'tight crop on one piece'},
    ],
    directions: ['kids crafts', 'dried oranges', 'felt ornaments'],
    directionHint: 'try: kids crafts',
    seo: {
        title: 'DIY Christmas Decorations: Paper Stars, Wreath, Salt Dough',
        metaDescription: 'Nine photos of homemade Christmas decorations: paper stars, a pinecone wreath and salt-dough ornaments, in daylight, evening light and close up.',
        h1: 'DIY Christmas decorations: paper stars, pinecone wreath, salt-dough ornaments',
        tagline: 'Three crafts shown finished in daylight, in evening light and as close looks at the handmade texture.',
        intro: 'Three classic home crafts for Christmas: paper star lanterns in a window, a wreath of pinecones on a door and salt-dough ornaments on a tree. Each is shown finished in daylight, glowing in evening light and in a close look at the handmade texture. Families use them to choose a craft to make together. Type a twist, such as kids crafts or felt ornaments, and the crafts are asked to follow it.',
        useCases: [
            'a craft for a family afternoon',
            'a school or club project for the holidays',
            'a handmade gift',
            'a window or door decoration for the season',
        ],
        faq: [
            {q: 'What crafts are shown?', a: 'Paper star lanterns in a window, a pinecone wreath on a door, and salt-dough ornaments on a tree.'},
            {q: 'Do the pictures show how to make them?', a: 'No. The pictures show the finished pieces in place and do not give steps or materials lists.'},
            {q: 'What do the three styles show?', a: 'In daylight shows the piece on a winter morning. Evening glow shows it lit by fairy lights from behind. Close-up is a tight look at one piece.'},
            {q: 'Can I ask for a different craft?', a: 'Yes. Type a twist, such as dried oranges, and the page asks for a craft that follows it.'},
        ],
    },
}
